import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { TextDisplayCard } from 'components/TextDisplayCard/TextDisplayCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import useModalClose from 'hooks/useModalClose'
import { CursorPointer, Flex, ItemsGrid, ModalScrollContainer, ThinSeparator } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import {
  IInventoryTransferControl,
  IInventoryTransferTimelineStep,
  IViewInventoryTransferModalProps,
} from 'types/inventoryControl.interface'
import { InventoryTransferStatus } from 'types/inventoryEnums.interface'
import { formatDateForDisplay } from 'utils/helpers'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { TRANSFER_STATUS_META } from '../Shared/inventoryEnumMaps'
import { TRANSFER_RECEIPT_OK_NOTE } from '../Shared/inventoryFormatters'
import {
  ReceiptActionWrap,
  TimelineDot,
  TimelineLabel,
  TimelineNote,
  TimelineRow,
  TimelineTime,
  TimelineWrap,
} from './ViewInventoryTransferModal.styled'

const TRANSFER_TIMELINE_ORDER: InventoryTransferStatus[] = [
  InventoryTransferStatus.Dispatched,
  InventoryTransferStatus.InTransit,
  InventoryTransferStatus.Received,
]

const buildTimeline = (control: IInventoryTransferControl): IInventoryTransferTimelineStep[] => {
  const statusIdx = TRANSFER_TIMELINE_ORDER.indexOf(control.status)
  return TRANSFER_TIMELINE_ORDER.map((key, idx) => {
    const state: IInventoryTransferTimelineStep['state'] =
      idx < statusIdx
        ? 'done'
        : idx === statusIdx
          ? key === InventoryTransferStatus.InTransit
            ? 'active'
            : 'done'
          : 'pending'
    let time: string | null = null
    if (key === InventoryTransferStatus.Dispatched && statusIdx >= 0) time = control.dispatchedAt
    if (key === InventoryTransferStatus.InTransit && control.status === InventoryTransferStatus.InTransit) {
      time = `On the road since ${control.dispatchedAt ?? '—'}`
    }
    if (key === InventoryTransferStatus.Received && control.receivedAt) {
      time = control.receivedBy
        ? `${control.receivedAt} · ${control.receivedBy}`
        : control.receivedAt
    }
    return {
      key,
      label: TRANSFER_STATUS_META[key]?.label ?? String(key),
      state,
      time,
    }
  })
}

export const ViewInventoryTransferModal = ({
  closeModal,
  value,
  onConfirmReceipt,
}: IViewInventoryTransferModalProps) => {
  useFocusFirstInput()
  useModalClose(closeModal)

  const control = value.control
  const meta = control ? TRANSFER_STATUS_META[control.status] : null
  const canConfirm =
    control &&
    control.status !== InventoryTransferStatus.Received &&
    onConfirmReceipt

  return (
    <Modal black>
      <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
        <Flex center gap='0.75rem'>
          <TitleText weight='bold'>Transfer Preview</TitleText>
          {meta && (
            <StatusPill
              status={meta.tone}
              dot={control.status === InventoryTransferStatus.InTransit}
            >
              {meta.label}
            </StatusPill>
          )}
        </Flex>
        <CursorPointer onClick={closeModal}>
          <CloseIcon />
        </CursorPointer>
      </Flex>
      <ModalScrollContainer>
        <ItemsGrid columns={2}>
          <TextDisplayCard title={formatDateForDisplay(value.date)} labelText='Date' />
          <TextDisplayCard
            title={
              value.performedByUser
                ? `${value.performedByUser.firstName} ${value.performedByUser.lastName}`
                : '/'
            }
            labelText='Performed By'
          />
          <TextDisplayCard
            title={
              value.assignedForUser
                ? `${value.assignedForUser.firstName} ${value.assignedForUser.lastName}`
                : '/'
            }
            labelText='Assigned Employee'
          />
          <TextDisplayCard
            title={value.fromLocation ? value.fromLocation.name : '/'}
            labelText='From location'
          />
          <TextDisplayCard
            title={value.toLocation ? value.toLocation.name : '/'}
            labelText='To location'
          />
        </ItemsGrid>

        <TextDisplayCard
          style={{ marginTop: '1rem' }}
          title={value.notes || '/'}
          labelText='Notes'
          width='100%'
        />

        {control && (
          <>
            <ThinSeparator />
            <Flex column gap='1rem'>
              <BodyText weight='bold'>Custody:</BodyText>
              <TimelineWrap>
                {buildTimeline(control).map((step) => (
                  <TimelineRow key={step.key}>
                    <TimelineDot $state={step.state} />
                    <TimelineLabel $dim={step.state === 'pending'}>{step.label}</TimelineLabel>
                    <TimelineTime $dim={step.state === 'pending'}>
                      {step.time ?? '—'}
                    </TimelineTime>
                  </TimelineRow>
                ))}
                {control.status === InventoryTransferStatus.Received && (
                  <TimelineNote
                    $warning={
                      !!control.varianceNote && control.varianceNote !== TRANSFER_RECEIPT_OK_NOTE
                    }
                  >
                    {control.varianceNote ?? 'No variance'}
                  </TimelineNote>
                )}
              </TimelineWrap>
              {canConfirm && (
                <ReceiptActionWrap>
                  <OtherText grey>Counted at destination — confirm to close custody</OtherText>
                  <Button size='sm' variant='primary' onClick={() => onConfirmReceipt(value)}>
                    Confirm receipt
                  </Button>
                </ReceiptActionWrap>
              )}
            </Flex>
          </>
        )}

        <ThinSeparator />
        <Flex column gap='1rem'>
          <BodyText weight='bold'>Items:</BodyText>
          {value.lines.map((l) => (
            <Flex key={l.id} column width='100%'>
              <Flex width='100%' gap='1rem'>
                <TextDisplayCard
                  width='100%'
                  title={l.inventoryItem ? l.inventoryItem.name : '/'}
                  labelText='Item'
                />
                <TextDisplayCard width='100%' title={l.quantity} labelText='Quantity' />
              </Flex>
              <TextDisplayCard
                style={{ marginTop: '1rem' }}
                title={l.lineNotes || '/'}
                labelText='Notes'
                width='100%'
              />
              <ThinSeparator margin='0.5rem 0' />
            </Flex>
          ))}
        </Flex>
      </ModalScrollContainer>
    </Modal>
  )
}
